import { todayISO as melToday } from "@/lib/intl";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { RefreshCw, DollarSign, AlertTriangle, Download, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/intl";
import { cn } from "@/lib/utils";
import { getCached, setCached } from "@/lib/pageStateCache";

const SERIES_COLORS = [
  "#18181b", "#10b981", "#3b82f6", "#f59e0b",
  "#a855f7", "#ec4899", "#06b6d4", "#84cc16", "#94a3b8",
];

const MODE_OPTIONS = [
  { value: "rolling", label: "Rolling window (monthly)" },
  { value: "rolling_weeks", label: "Rolling window (weekly)" },
  { value: "trailing_days", label: "Trailing rolling sum (daily)" },
  { value: "year_compare", label: "Compare two calendar years" },
  { value: "month_compare", label: "Compare same month across years" },
];

const ROLLING_OPTIONS = [
  { value: 3, label: "Last 3 months" },
  { value: 6, label: "Last 6 months" },
  { value: 12, label: "Last 12 months" },
];

const ROLLING_WEEK_OPTIONS = [
  { value: 4,  label: "Last 4 weeks" },
  { value: 8,  label: "Last 8 weeks" },
  { value: 12, label: "Last 12 weeks" },
  { value: 26, label: "Last 26 weeks" },
  { value: 52, label: "Last 52 weeks" },
];

const TRAILING_WINDOW_OPTIONS = [
  { value: 7,  label: "7-day trailing sum" },
  { value: 14, label: "14-day trailing sum" },
  { value: 30, label: "30-day trailing sum" },
  { value: 60, label: "60-day trailing sum" },
  { value: 90, label: "90-day trailing sum" },
];

const TRAILING_RANGE_OPTIONS = [
  { value: 30,  label: "Last 30 days" },
  { value: 60,  label: "Last 60 days" },
  { value: 90,  label: "Last 90 days" },
  { value: 180, label: "Last 6 months" },
  { value: 365, label: "Last 12 months" },
  { value: 730, label: "Last 24 months" },
];

const SOURCE_OPTIONS = [
  {
    value: "sales",
    label: "Revenue by sales",
    hint: "Acuity orders + Stripe charges (sale date)",
  },
  {
    value: "appointment",
    label: "Revenue by appointment",
    hint: "Appointment amountPaid (service date)",
  },
];

const DIMENSION_OPTIONS = [
  { value: "total", label: "Just total" },
  { value: "product", label: "By product / type" },
  { value: "provider", label: "By provider (appointment only)" },
  { value: "calendar", label: "By calendar (appointment only)" },
  { value: "payment_method", label: "By payment method (sales only)" },
];

const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const SOURCE_LABEL = {
  amountPaid: "Direct payment",
  certificate: "Cert redemption",
  certificate_from_order: "Cert (recovered from order)",
  value_subscription: "Value subscription",
  appointment_type: "Cert fallback (list price)",
  value_subscription_unresolved: "Value sub (unresolved)",
  unknown: "Unknown source",
};

function StatCard({ label, value, hint, icon: Icon, testId, deltaBadge }) {
  return (
    <Card className="p-5 rounded-lg border border-zinc-200 shadow-none" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
        {Icon && <Icon className="h-3.5 w-3.5" />} {label}
      </div>
      <div className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-950 font-mono-num">
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-zinc-500">{hint}</div>}
      {deltaBadge && <div className="mt-2">{deltaBadge}</div>}
    </Card>
  );
}


/**
 * Small badge summarising current vs previous revenue delta so the
 * clinician can eyeball direction + magnitude without reading tooltips.
 */
function RevenueDeltaBadge({ current, previous }) {
  if (previous === 0 && current === 0) return null;
  const delta = current - previous;
  const pct = previous > 0 ? Math.round((delta / previous) * 100) : null;
  const up = delta > 0;
  const flat = Math.abs(delta) < 0.005;
  const cls = flat
    ? "border-zinc-200 bg-zinc-50 text-zinc-700"
    : up
      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
      : "border-rose-200 bg-rose-50 text-rose-900";
  const arrow = flat ? "→" : up ? "↑" : "↓";
  const pctLabel = pct === null ? "n/a" : `${up ? "+" : ""}${pct}%`;
  return (
    <span
      className={`inline-block text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}
      data-testid="revenue-delta-badge"
      title={`Previous period: ${fmtMoney(previous)}; delta ${up ? "+" : ""}${fmtMoney(delta)}`}
    >
      {arrow} {up ? "+" : ""}{fmtMoney(delta)} vs prev · {pctLabel}
    </span>
  );
}

export default function RevenueReportTab() {
  const today = useMemo(() => new Date(), []);
  const currentYear = today.getFullYear();

  const cached = getCached("reports-revenue");

  // Form state
  const [source, setSource] = useState(cached?.source ?? "sales");
  const [dimension, setDimension] = useState(cached?.dimension ?? "total");
  const [mode, setMode] = useState(cached?.mode ?? "rolling");
  const [rollingMonths, setRollingMonths] = useState(cached?.rollingMonths ?? 12);
  const [rollingWeeks, setRollingWeeks] = useState(cached?.rollingWeeks ?? 12);
  const [trailingWindow, setTrailingWindow] = useState(cached?.trailingWindow ?? 30);
  const [trailingRange, setTrailingRange] = useState(cached?.trailingRange ?? 365);
  const [anchorDate, setAnchorDate] = useState(cached?.anchorDate ?? "");
  const [yearA, setYearA] = useState(cached?.yearA ?? String(currentYear - 1));
  const [yearB, setYearB] = useState(cached?.yearB ?? String(currentYear));
  const [monthIdx, setMonthIdx] = useState(cached?.monthIdx ?? String(today.getMonth() + 1));
  const [nYears, setNYears] = useState(cached?.nYears ?? "3");
  const [gstMode, setGstMode] = useState(cached?.gstMode ?? "inc");  // "inc" | "ex"
  const [comparePrevious, setComparePrevious] = useState(
    cached?.comparePrevious ?? false,
  );

  // Result state
  const [data, setData] = useState(cached?.data ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Persist form + result so navigating away & back keeps the report loaded.
  useEffect(() => {
    setCached("reports-revenue", {
      source, dimension, mode, rollingMonths, rollingWeeks, trailingWindow, trailingRange,
      anchorDate,
      yearA, yearB, monthIdx, nYears, gstMode, comparePrevious, data,
      ts: Date.now(),
    });
  }, [source, dimension, mode, rollingMonths, rollingWeeks, trailingWindow, trailingRange,
      anchorDate,
      yearA, yearB, monthIdx, nYears, gstMode, comparePrevious, data]);

  // Auto-correct invalid source/dimension combos as the user changes them.
  useEffect(() => {
    if (source === "sales" && (dimension === "provider" || dimension === "calendar")) {
      setDimension("total");
    }
    if (source === "appointment" && dimension === "payment_method") {
      setDimension("total");
    }
  }, [source, dimension]);

  async function runReport() {
    setLoading(true);
    setError(null);
    try {
      const params = { source, dimension, mode };
      if (mode === "rolling") params.months = rollingMonths;
      if (mode === "rolling_weeks") params.weeks = rollingWeeks;
      if (mode === "trailing_days") {
        params.trailing_window = trailingWindow;
        params.range_days = trailingRange;
      }
      if (mode === "year_compare") {
        params.year_a = Number(yearA);
        params.year_b = Number(yearB);
      }
      if (mode === "month_compare") {
        params.month_idx = Number(monthIdx);
        params.n_years = Number(nYears);
      }
      if (comparePrevious && (mode === "rolling" || mode === "rolling_weeks" || mode === "trailing_days")) {
        params.compare_previous = true;
      }
      if (anchorDate && ["rolling", "rolling_weeks", "trailing_days"].includes(mode)) {
        params.anchor_date = anchorDate;
      }
      const r = await api.get("/reports/revenue", { params, timeout: 180000 });
      setData(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(msg);
      toast.error("Revenue report failed", { description: msg });
    } finally {
      setLoading(false);
    }
  }

  function exportCsv() {
    if (!data) return;
    const lines = [];
    const xa = data.x_axis || [];
    const gstSuffix = gstMode === "ex" ? "ex-GST" : "inc-GST";
    if (data.dimension === "total") {
      lines.push(["Period", `Revenue (AUD, ${gstSuffix})`].join(","));
      (data.table_rows || []).forEach((r) => {
        const total = (r.row_total ?? 0) * gstScale;
        lines.push([csvSafe(r.label), total.toFixed(2)].join(","));
      });
      lines.push(["Grand total", ((data.grand_total ?? 0) * gstScale).toFixed(2)].join(","));
    } else {
      // Cross-tab
      lines.push(["Dimension", ...xa, `Total (${gstSuffix})`].map(csvSafe).join(","));
      (data.table_rows || []).forEach((r) => {
        const vals = (r.values_per_x || []).map((v) => v * gstScale);
        lines.push(
          [csvSafe(r.label), ...vals.map((v) => v.toFixed(2)), ((r.row_total ?? 0) * gstScale).toFixed(2)].join(",")
        );
      });
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `revenue_${data.source}_${data.dimension}_${data.mode}_${data.period_start}_${data.period_end}_${gstSuffix}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success("CSV downloaded");
  }

  // Build the recharts dataset: [{name: "Mar 2025", series1: 1000, series2: 500}, ...]
  const gstScale = useMemo(() => {
    if (gstMode !== "ex" || !data) return 1;
    const r = typeof data.gst_rate === "number" ? data.gst_rate : 0.10;
    return 1 / (1 + r);
  }, [gstMode, data]);

  const chartData = useMemo(() => {
    if (!data) return [];
    const xa = data.x_axis || [];
    const series = data.series || [];
    const prevSeries = data.previous?.series || [];
    return xa.map((label, i) => {
      const row = { name: label };
      series.forEach((s) => {
        row[s.name] = (s.data?.[i] ?? 0) * gstScale;
      });
      if (data.previous) {
        row["Previous total"] = prevSeries.reduce(
          (a, s) => a + ((Number(s.data?.[i]) || 0) * gstScale), 0,
        );
      }
      return row;
    });
  }, [data, gstScale]);

  const seriesList = data?.series || [];
  const showStacked = data && data.dimension !== "total" && data.mode !== "year_compare";

  return (
    <div className="space-y-6" data-testid="revenue-report-tab">
      {/* Form */}
      <Card className="p-5 sm:p-6 rounded-lg border border-zinc-200 shadow-none">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {/* Source */}
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Revenue source</Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="border-zinc-300 mt-1.5" data-testid="revenue-source-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOURCE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-2xs text-zinc-500">
              {SOURCE_OPTIONS.find((o) => o.value === source)?.hint}
            </p>
          </div>

          {/* Dimension */}
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Breakdown</Label>
            <Select value={dimension} onValueChange={setDimension}>
              <SelectTrigger className="border-zinc-300 mt-1.5" data-testid="revenue-dimension-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIMENSION_OPTIONS.map((o) => {
                  const disabled =
                    (source === "sales" && (o.value === "provider" || o.value === "calendar")) ||
                    (source === "appointment" && o.value === "payment_method");
                  return (
                    <SelectItem key={o.value} value={o.value} disabled={disabled}>
                      {o.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Mode */}
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Period mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="border-zinc-300 mt-1.5" data-testid="revenue-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Mode-specific inputs */}
          {mode === "rolling" && (
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">Window</Label>
              <Select value={String(rollingMonths)} onValueChange={(v) => setRollingMonths(Number(v))}>
                <SelectTrigger className="border-zinc-300 mt-1.5" data-testid="revenue-rolling-months">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLLING_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={String(o.value)}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {mode === "rolling_weeks" && (
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">Window</Label>
              <Select value={String(rollingWeeks)} onValueChange={(v) => setRollingWeeks(Number(v))}>
                <SelectTrigger className="border-zinc-300 mt-1.5" data-testid="revenue-rolling-weeks">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLLING_WEEK_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={String(o.value)}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {mode === "trailing_days" && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">
                  Trailing sum
                </Label>
                <Select
                  value={String(trailingWindow)}
                  onValueChange={(v) => setTrailingWindow(Number(v))}
                >
                  <SelectTrigger className="border-zinc-300 mt-1.5" data-testid="revenue-trailing-window">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRAILING_WINDOW_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">
                  Timeline
                </Label>
                <Select
                  value={String(trailingRange)}
                  onValueChange={(v) => setTrailingRange(Number(v))}
                >
                  <SelectTrigger className="border-zinc-300 mt-1.5" data-testid="revenue-trailing-range">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRAILING_RANGE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 text-2xs text-zinc-500 -mt-1">
                Each dot shows the total revenue over the previous{" "}
                <span className="font-medium text-zinc-700">{trailingWindow}</span> days
                as of that date — smooths out day-of-week noise.
              </div>
            </div>
          )}

          {["rolling", "rolling_weeks", "trailing_days"].includes(mode) && (
            <div data-testid="revenue-anchor-row">
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">
                Anchor to date <span className="normal-case text-zinc-400">(optional)</span>
              </Label>
              <div className="flex items-center gap-1.5 mt-1.5">
                <Input
                  type="date"
                  value={anchorDate}
                  max={melToday()}
                  onChange={(e) => setAnchorDate(e.target.value)}
                  className="border-zinc-300 font-mono-num"
                  data-testid="revenue-anchor-date"
                />
                {anchorDate && (
                  <Button
                    variant="outline" size="sm" className="h-9 text-2xs px-2"
                    onClick={() => setAnchorDate("")}
                    data-testid="revenue-anchor-clear"
                  >
                    Clear
                  </Button>
                )}
              </div>
              <div className="text-2xs text-zinc-500 mt-1">
                {anchorDate
                  ? `Freezes the window end at ${anchorDate} — useful for "trailing ${
                      mode === "trailing_days" ? trailingWindow
                      : mode === "rolling_weeks" ? `${rollingWeeks} weeks`
                      : `${rollingMonths} months`
                    } up to a specific date".`
                  : "Leave blank to end the window at today."}
              </div>
            </div>
          )}

          {mode === "year_compare" && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">Year A</Label>
                <Input
                  type="number"
                  value={yearA}
                  onChange={(e) => setYearA(e.target.value)}
                  className="border-zinc-300 mt-1.5 font-mono-num"
                  data-testid="revenue-year-a"
                />
              </div>
              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">Year B</Label>
                <Input
                  type="number"
                  value={yearB}
                  onChange={(e) => setYearB(e.target.value)}
                  className="border-zinc-300 mt-1.5 font-mono-num"
                  data-testid="revenue-year-b"
                />
              </div>
            </div>
          )}

          {mode === "month_compare" && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">Month</Label>
                <Select value={monthIdx} onValueChange={setMonthIdx}>
                  <SelectTrigger className="border-zinc-300 mt-1.5" data-testid="revenue-month-idx">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTH_LABELS.map((m, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500"># of years</Label>
                <Input
                  type="number"
                  min="2"
                  max="10"
                  value={nYears}
                  onChange={(e) => setNYears(e.target.value)}
                  className="border-zinc-300 mt-1.5 font-mono-num"
                  data-testid="revenue-n-years"
                />
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2 items-center">
          <Button
            onClick={runReport}
            disabled={loading}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="revenue-run-btn"
          >
            <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
            {loading ? "Running…" : data ? "Re-run report" : "Run report"}
          </Button>
          {data && (
            <Button
              variant="outline"
              onClick={exportCsv}
              className="border-zinc-300 hover:bg-zinc-100"
              data-testid="revenue-export-csv"
            >
              <Download className="h-4 w-4 mr-1.5" /> Export CSV
            </Button>
          )}

          {/* GST toggle — divides displayed numbers client-side by (1+gst_rate). */}
          <div className="inline-flex rounded-md border border-zinc-300 overflow-hidden text-xs font-mono-num" data-testid="revenue-gst-toggle">
            <button
              type="button"
              onClick={() => setGstMode("inc")}
              className={cn(
                "px-3 py-1.5 transition-colors",
                gstMode === "inc"
                  ? "bg-zinc-950 text-white"
                  : "bg-white text-zinc-700 hover:bg-zinc-100",
              )}
              data-testid="revenue-gst-inc"
            >
              Inc-GST
            </button>
            <button
              type="button"
              onClick={() => setGstMode("ex")}
              className={cn(
                "px-3 py-1.5 transition-colors border-l border-zinc-300",
                gstMode === "ex"
                  ? "bg-zinc-950 text-white"
                  : "bg-white text-zinc-700 hover:bg-zinc-100",
              )}
              data-testid="revenue-gst-ex"
            >
              Ex-GST
            </button>
          </div>

          {/* Compare-to-previous — only meaningful on rolling modes;
              year_compare / month_compare are already comparisons. */}
          {(mode === "rolling" || mode === "rolling_weeks" || mode === "trailing_days") && (
            <label
              className="inline-flex items-center gap-2 text-xs text-zinc-700 cursor-pointer"
              title="Overlay a faded bar showing the same-length window immediately before the current one, so you can see if this stretch is bigger or smaller than the last."
              data-testid="revenue-compare-previous-label"
            >
              <input
                type="checkbox"
                className="h-3.5 w-3.5 accent-indigo-600"
                checked={comparePrevious}
                onChange={(e) => setComparePrevious(e.target.checked)}
                data-testid="revenue-compare-previous"
              />
              Compare to previous period
            </label>
          )}

          {data && (
            <div className="ml-auto text-xs text-zinc-500 font-mono-num" data-testid="revenue-period-summary">
              {data.period_start} → {data.period_end}
              {data.anchor_date && (
                <span className="ml-1 text-indigo-700">· anchored to {data.anchor_date}</span>
              )}
              {" · "}{fetchedSummary(data.fetched)}
            </div>
          )}
        </div>
      </Card>

      {/* Error */}
      {error && (
        <div className="p-4 rounded-md border border-red-200 bg-red-50 text-sm text-red-700" data-testid="revenue-error">
          {error}
        </div>
      )}

      {/* Idle */}
      {!data && !loading && !error && (
        <Card className="p-12 rounded-lg border-2 border-dashed border-zinc-200 bg-zinc-50 text-center" data-testid="revenue-idle">
          <DollarSign className="h-8 w-8 text-zinc-300 mx-auto mb-3" strokeWidth={1.5} />
          <h2 className="font-display text-xl font-semibold tracking-tight text-zinc-950">
            Pick your options and click "Run report"
          </h2>
          <p className="mt-2 text-sm text-zinc-600 max-w-md mx-auto">
            Sales source pulls Acuity orders + Stripe charges; appointment source uses each appointment's
            amountPaid. Year and month comparisons fetch enough Acuity data to span the chosen window
            (10-60 seconds).
          </p>
        </Card>
      )}

      {/* Loading */}
      {loading && (
        <Card className="p-12 rounded-lg border border-zinc-200 shadow-none text-center" data-testid="revenue-loading">
          <RefreshCw className="h-8 w-8 text-zinc-400 mx-auto mb-3 animate-spin" strokeWidth={1.5} />
          <p className="font-mono-num text-sm text-zinc-600">
            Querying Acuity{source === "sales" ? " + Stripe" : ""} · this can take up to 60 seconds
          </p>
        </Card>
      )}

      {/* Results */}
      {data && (
        <>
          {/* Warnings */}
          {data.warnings && data.warnings.length > 0 && (
            <Card className="p-4 rounded-md border border-amber-200 bg-amber-50 shadow-none" data-testid="revenue-warnings">
              <div className="flex items-start gap-2 text-sm text-amber-900">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <ul className="space-y-1">
                  {data.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            </Card>
          )}

          {/* Source breakdown (Appointment mode) - audit which mechanism each $ came from */}
          {data.fetched?.source_breakdown && Object.keys(data.fetched.source_breakdown).length > 0 && (
            <Card className="p-5 rounded-lg border border-zinc-200 shadow-none" data-testid="revenue-source-breakdown">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-3">
                Revenue source breakdown (audit) · {gstMode === "ex" ? "Ex-GST" : "Inc-GST"}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {Object.entries(data.fetched.source_breakdown)
                  .sort((a, b) => b[1] - a[1])
                  .map(([src, amt]) => (
                    <div key={src} className="p-3 rounded border border-zinc-200 bg-zinc-50/60">
                      <div className="text-2xs uppercase tracking-wider text-zinc-500 truncate" title={src}>
                        {SOURCE_LABEL[src] || src}
                      </div>
                      <div className="font-mono-num text-base font-semibold text-zinc-900 mt-0.5">
                        {fmtMoney(amt * gstScale)}
                      </div>
                    </div>
                  ))}
              </div>
              <p className="mt-3 text-2xs text-zinc-500">
                <span className="font-semibold">amountPaid</span> = direct cash/Stripe ·
                <span className="font-semibold"> certificate</span> = clean pack/cert redemption ·
                <span className="font-semibold"> certificate_from_order</span> = recovered via original order ·
                <span className="font-semibold"> appointment_type</span> = list-price fallback (legit cert but lookup failed) ·
                <span className="font-semibold"> value_subscription</span> = value-based sub
              </p>
            </Card>
          )}

          {/* Stat cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              label={`Grand total · ${gstMode === "ex" ? "Ex-GST" : "Inc-GST"}`}
              value={fmtMoney((data.grand_total || 0) * gstScale)}
              hint={`${data.source === "sales" ? "Orders + Stripe" : "Appointments"} · ${data.dimension}`}
              icon={DollarSign}
              testId="revenue-grand-total"
              deltaBadge={data.previous
                ? <RevenueDeltaBadge
                    current={(data.grand_total || 0) * gstScale}
                    previous={(data.previous.grand_total || 0) * gstScale}
                  />
                : null}
            />
            <StatCard
              label="Best period"
              value={bestPeriod(data, gstScale)?.label || "—"}
              hint={bestPeriod(data, gstScale) ? fmtMoney(bestPeriod(data, gstScale).total) : ""}
              icon={TrendingUp}
              testId="revenue-best-period"
            />
            <StatCard
              label="Average per period"
              value={fmtMoney(avgPerPeriod(data) * gstScale)}
              hint={`across ${data.x_axis?.length || 0} periods`}
              testId="revenue-avg-period"
            />
          </div>

          {/* Bar chart */}
          <Card className="p-5 rounded-lg border border-zinc-200 shadow-none" data-testid="revenue-chart">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-3">
              {data.mode === "trailing_days"
                ? `Trailing ${data.trailing_window || ""}-day rolling revenue`
                : data.mode === "rolling_weeks"
                  ? "Weekly chart"
                  : "Monthly chart"}
            </div>
            {chartData.length === 0 ? (
              <div className="text-sm text-zinc-500 py-12 text-center">No data in this window.</div>
            ) : data.mode === "trailing_days" ? (
              // Line chart: with up to ~730 daily points, bars are unreadable.
              // Skip x-axis tick labels for very dense timelines so the line
              // doesn't smash into an ant-farm of overlapping dates.
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
                    <CartesianGrid stroke="#e4e4e7" strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: "#52525b" }}
                      interval={Math.max(0, Math.floor(chartData.length / 12) - 1)}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#52525b" }}
                      tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
                    />
                    <Tooltip
                      formatter={(value) => fmtMoney(value)}
                      contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e4e4e7" }}
                    />
                    {seriesList.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
                    {seriesList.map((s, i) => (
                      <Line
                        key={s.name}
                        type="monotone"
                        dataKey={s.name}
                        stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
                    <CartesianGrid stroke="#e4e4e7" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#52525b" }} />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#52525b" }}
                      tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
                    />
                    <Tooltip
                      formatter={(value) => fmtMoney(value)}
                      contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e4e4e7" }}
                    />
                    {seriesList.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
                    {seriesList.map((s, i) => (
                      <Bar
                        key={s.name}
                        dataKey={s.name}
                        fill={SERIES_COLORS[i % SERIES_COLORS.length]}
                        stackId={showStacked ? "stack" : undefined}
                        radius={i === seriesList.length - 1 ? [4, 4, 0, 0] : 0}
                      />
                    ))}
                    {data.previous && (
                      <Bar
                        dataKey="Previous total"
                        stackId="prev"
                        fill="#cbd5e1"
                        fillOpacity={0.75}
                        radius={[4, 4, 0, 0]}
                      />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {data.previous && (
              <div className="mt-2 text-2xs text-zinc-500 px-1" data-testid="revenue-previous-window">
                Comparing against {data.previous.window?.start} → {data.previous.window?.end}{" "}
                (previous total {fmtMoney((data.previous.grand_total || 0) * gstScale)}).
              </div>
            )}
          </Card>

          {/* Table */}
          <Card className="rounded-lg border border-zinc-200 shadow-none overflow-hidden" data-testid="revenue-table">
            <div className="p-5 border-b border-zinc-200 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
              {data.dimension === "total" ? "Monthly breakdown" : `Breakdown by ${data.dimension}`}
              {" · "}{gstMode === "ex" ? "Ex-GST" : "Inc-GST"}
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50 hover:bg-zinc-50">
                    {data.dimension === "total" ? (
                      <>
                        <TableHead className="text-xs font-semibold uppercase text-zinc-600">Period</TableHead>
                        {data.series?.map((s) => (
                          <TableHead key={s.name} className="text-xs font-semibold uppercase text-zinc-600 text-right">
                            {s.name}
                          </TableHead>
                        ))}
                        <TableHead className="text-xs font-semibold uppercase text-zinc-600 text-right">Total</TableHead>
                      </>
                    ) : (
                      <>
                        <TableHead className="text-xs font-semibold uppercase text-zinc-600">
                          {dimensionLabel(data.dimension)}
                        </TableHead>
                        {data.x_axis?.map((x) => (
                          <TableHead key={x} className="text-xs font-semibold uppercase text-zinc-600 text-right">
                            {x}
                          </TableHead>
                        ))}
                        <TableHead className="text-xs font-semibold uppercase text-zinc-600 text-right">Total</TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.table_rows?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={20} className="py-8 text-center text-sm text-zinc-500">
                        No revenue in this window.
                      </TableCell>
                    </TableRow>
                  ) : data.dimension === "total" ? (
                    data.table_rows.map((r) => (
                      <TableRow key={r.key}>
                        <TableCell className="font-mono-num text-sm">{r.label}</TableCell>
                        {data.series?.map((s) => (
                          <TableCell key={s.name} className="font-mono-num text-sm text-right">
                            {fmtMoney((r.values?.[s.name] ?? 0) * gstScale)}
                          </TableCell>
                        ))}
                        <TableCell className="font-mono-num text-sm text-right font-semibold">
                          {fmtMoney((r.row_total ?? 0) * gstScale)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    data.table_rows.map((r) => (
                      <TableRow key={r.label}>
                        <TableCell className="text-sm">{r.label}</TableCell>
                        {(r.values_per_x || []).map((v, i) => (
                          <TableCell key={i} className="font-mono-num text-sm text-right">
                            {fmtMoney(v * gstScale)}
                          </TableCell>
                        ))}
                        <TableCell className="font-mono-num text-sm text-right font-semibold">
                          {fmtMoney((r.row_total ?? 0) * gstScale)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ---- helpers --------------------------------------------------------------
function csvSafe(v) {
  const s = String(v ?? "");
  return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
}

function fetchedSummary(fetched) {
  if (!fetched) return "";
  return Object.entries(fetched)
    .map(([k, v]) => `${v} ${k}`)
    .join(" · ");
}

function bestPeriod(data, gstScale = 1) {
  const xa = data.x_axis || [];
  const totals = xa.map((_, i) =>
    (data.series || []).reduce((acc, s) => acc + (s.data?.[i] ?? 0), 0) * gstScale
  );
  let bestIdx = -1;
  let bestVal = -Infinity;
  totals.forEach((v, i) => {
    if (v > bestVal) {
      bestVal = v;
      bestIdx = i;
    }
  });
  if (bestIdx < 0 || bestVal <= 0) return null;
  return { label: xa[bestIdx], total: totals[bestIdx] };
}

function avgPerPeriod(data) {
  const n = data.x_axis?.length || 0;
  if (n === 0) return 0;
  return (data.grand_total || 0) / n;
}

function dimensionLabel(d) {
  return {
    total: "Period",
    product: "Product / type",
    provider: "Provider",
    calendar: "Calendar",
    payment_method: "Payment method",
  }[d] || d;
}
